import type { FocusedTypes } from './../../constants'
import type {
  Account,
  AccountId,
  Group,
  GroupId,
  PreparedSearchAccountEntry,
  PreparedSearchGroupEntry,
  PreparedSearchEntry,
  CapitalisedLocale,
} from '../../shared-types'
import { isAccount, isGroup } from '../../shared-types'

import fuzzysort from 'fuzzysort'

import { focusedTypes } from '@app/application/account-mapping/constants'
import { firstAvailableLocalisedName } from '../../utils'

const prepareSearchAccountItem = ({
  itemId,
  item,
  order,
  supportedLocales,
}: {
  itemId: AccountId
  item: Account
  order: number
  supportedLocales: CapitalisedLocale[]
}): PreparedSearchAccountEntry => {
  const name = firstAvailableLocalisedName({
    item,
    nameKey: 'originalName',
    supportedLocales,
  })

  return {
    itemId,
    order,
    preparedNumber: fuzzysort.prepare(item.number),
    preparedOriginalName: fuzzysort.prepare(name),
    type: focusedTypes.ACCOUNT,
  }
}

const prepareSearchGroupItem = ({
  itemId,
  item,
  order,
  supportedLocales,
}: {
  itemId: GroupId
  item: Group
  order: number
  supportedLocales: CapitalisedLocale[]
}): PreparedSearchGroupEntry => {
  const name = firstAvailableLocalisedName({
    item,
    nameKey: 'name',
    supportedLocales,
  })

  return {
    itemId,
    order,
    preparedAccountRange: fuzzysort.prepare(item.accountRange),
    preparedName: fuzzysort.prepare(name),
    type: focusedTypes.GROUP,
  }
}

export const prepareSearchItem = ({
  itemId,
  item,
  order,
  type,
  supportedLocales,
}: {
  itemId: string
  item: Account | Group
  order: number
  type: FocusedTypes
  supportedLocales: CapitalisedLocale[]
}): PreparedSearchEntry | null => {
  if (isAccount(item) && type === focusedTypes.ACCOUNT) {
    return prepareSearchAccountItem({
      itemId,
      item,
      order,
      supportedLocales,
    })
  }

  if (isGroup(item) && type === focusedTypes.GROUP) {
    return prepareSearchGroupItem({
      itemId,
      item,
      order,
      supportedLocales,
    })
  }

  return null
}
